import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { useImageDropPaste } from '@/hooks/useImageDropPaste';
import { DropOverlay } from '@/components/ui/drop-overlay';

interface ImageDropZoneProps {
  /** Receives dropped/pasted files — same handler the surface passes to `ImageUploadInput`. */
  onFiles: (files: File[]) => void | Promise<void>;
  disabled?: boolean;
  /** Overlay caption; `null` (the default) shows only the ring, for small targets. */
  label?: string | null;
  className?: string;
  /** Rounding for the overlay to match the target. */
  overlayClassName?: string;
  children: ReactNode;
}

/**
 * Drop-in wrapper that makes its children a drag-and-drop + paste image target,
 * for surfaces that render the same picker many times (e.g. one per list row)
 * where a per-instance `useImageDropPaste` call isn't possible. Wraps the hook
 * and the shared highlight in a focusable `relative` container.
 */
export function ImageDropZone({
  onFiles,
  disabled = false,
  label = null,
  className,
  overlayClassName,
  children,
}: ImageDropZoneProps) {
  const { isDragging, dropZoneProps, onPaste } = useImageDropPaste({ onFiles, disabled });
  return (
    <div
      className={cn('relative outline-none focus-visible:ring-2 focus-visible:ring-stone-300', className)}
      tabIndex={disabled ? undefined : 0}
      {...(disabled ? {} : dropZoneProps)}
      onPaste={disabled ? undefined : onPaste}
    >
      {children}
      <DropOverlay active={isDragging} label={label} className={overlayClassName} />
    </div>
  );
}
